/**
 * FrameworkSection — extracted from TeacherDashboard.js (Feb 2026 refactor).
 *
 * Renders ONE section card in the Teacher Content Editor: header, content
 * editor, items grid (cards/problems/flashcards/etc.), image gallery,
 * tables (with AI palette generator), notes, drawings, charts, attachments,
 * equations and reference links — the entire editing surface for a single
 * `framework.sections[i]` entry.
 *
 * Lives in `components/teacher/ContentEditor/` alongside the AttachmentCard
 * family and small helpers it uses (EditFieldInput, AttachIcon, …) which
 * have no callers outside this file.
 *
 * Public API: default export `FrameworkSection`. All state mutations are
 * surfaced via callback props (`onAddItem`, `onUpdateTable`, …) so the
 * parent ContentEditor keeps full control of the draft document.
 */
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { confirmDialog } from '../../ConfirmDialog';
import {
  CheckCircle, Edit, Trash2, ChevronDown, ChevronRight, Plus, Save, BookOpen,
  Image, FileText, Globe, PlayCircle, Link2, BarChart3, Sigma,
  Paperclip, Pencil, Download, X, Image as Image2, Film, Music,
  Brain, Target, Star, Maximize2,
} from 'lucide-react';

import { RenderContent, MathText } from '../../RenderContent';
import RichTextEditor from './richEditor/RichTextEditor';
import { insertAtCursor, keepFocus, LineListEditor, SubsectionTextEditor } from './SectionTextEditors';
import SectionEquations from '../SectionEquations';
import DeskeyPaint from '../../DeskeyPaint';
import DeskeyChart from '../../DeskeyChart';
import SafeImage from '../../SafeImage';
import { getSectionIcon } from '../../../lib/sectionIcons';
import { friendlyError } from '../../../lib/errors';
import { teacherApi } from '../../../lib/teacherApi';
import { isInvalidLink, withSafeUrl } from '../../../lib/resourceLink';
import { ResourceLinkField } from './ResourceLink';
import { isSameItem, NO_EDIT_CHANGES } from './sectionEditState';
import { sectionTypeLabel } from './sectionTypes';
import { InlineSparkle } from '../../Loader';
import { FilePreviewSkeleton } from '../../Skeleton';

const API = process.env.REACT_APP_BACKEND_URL;
const G = '#34A853';
const REF_PICKER_WIDTH = 320; // px — the section's Insert Ref dropdown

const api = teacherApi;

// Section icons come from the shared registry in lib/sectionIcons.

// ────────────────────────────────────────────────────────────────────────────
// Resize — same 33/50/66/100% cycle as teacher-mobile's SectionMedia, so an
// image/chart/attachment can sit at partial width (e.g. two 50% side by
// side) instead of always filling the whole section.
// ────────────────────────────────────────────────────────────────────────────
const WIDTH_STEPS = [33, 50, 66, 100];
function nextWidth(current) {
  const idx = WIDTH_STEPS.indexOf(current);
  return WIDTH_STEPS[(idx + 1) % WIDTH_STEPS.length];
}
function ResizeButton({ width, onClick, dark }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg transition-colors ${dark ? 'text-white bg-black/35 hover:bg-black/50 backdrop-blur-sm' : 'text-slate-500 hover:bg-slate-100'}`}
      title={`Resize — currently ${width}%`}
    >
      <Maximize2 className="w-3 h-3" /> {width}%
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Difficulty palette — used by problem / flashcard cards.
// ────────────────────────────────────────────────────────────────────────────
const DIFF = {
  easy:   { bg: '#DCFCE7', text: '#166534', l: 'Easy' },
  medium: { bg: '#FEF9C3', text: '#854D0E', l: 'Medium' },
  hard:   { bg: '#FEE2E2', text: '#991B1B', l: 'Hard' },
};

// ────────────────────────────────────────────────────────────────────────────
// Table palette — every table renders in one fixed, clean, theme-based green
// look (no per-table custom styling — keeps every table visually consistent).
// ────────────────────────────────────────────────────────────────────────────
const TABLE_DEFAULT_COLORS = {
  header_bg: '#ecfdf5', header_text: '#065f46', accent: '#34A853',
  odd_row_bg: '#ffffff', even_row_bg: '#f0fdf4', text: '#1e293b', border: '#34A853',
};

// ────────────────────────────────────────────────────────────────────────────
// EditFieldInput — single-line / multiline edit input used in every item-edit
// form. Stops bubbling so clicking inside doesn't trigger row-level handlers.
// ────────────────────────────────────────────────────────────────────────────
function EditFieldInput({ value, onChange, placeholder, multiline, mono }) {
  if (multiline) return (
    <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className={`w-full text-xs px-3 py-2 rounded-lg border border-[#34A853]/20 bg-[#34A853]/5 resize-none focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40 ${mono ? 'font-mono' : ''}`} rows={3}
      onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} />
  );
  return (
    <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className={`w-full text-xs px-3 py-2 rounded-lg border border-[#34A853]/20 bg-[#34A853]/5 focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40 ${mono ? 'font-mono' : ''}`}
      onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} />
  );
}
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ATTACH_THEMES = {
  document: { bg: '#fef3c7', border: '#fde68a', text: '#92400e', ring: '#f59e0b' },
  image: { bg: '#dbeafe', border: '#bfdbfe', text: '#1e40af', ring: '#3b82f6' },
  video: { bg: '#fce7f3', border: '#fbcfe8', text: '#9d174d', ring: '#ec4899' },
  audio: { bg: '#ede9fe', border: '#ddd6fe', text: '#5b21b6', ring: '#8b5cf6' },
};

const AttachIcon = ({ category, className = 'w-5 h-5' }) => {
  if (category === 'image') return <Image2 className={className} />;
  if (category === 'video') return <Film className={className} />;
  if (category === 'audio') return <Music className={className} />;
  return <FileText className={className} />;
};

const buildAttachmentUrl = (attachmentId) => {
  const token = localStorage.getItem('desky_token') || '';
  return `${API}/api/teacher/studio/attachment/${attachmentId}/download?auth=${encodeURIComponent(token)}`;
};

export function AttachmentCard({ attachment, onOpen, onRenameStart, onRemove, onResize, isRenaming, renameTitle, setRenameTitle, onRenameCancel, onRenameSave, sectionId }) {
  const theme = ATTACH_THEMES[attachment.category] || ATTACH_THEMES.document;
  return (
    <div
      className="group/att rounded-xl border bg-white hover:border-slate-300 transition flex items-center gap-3 p-3 relative overflow-hidden"
      style={{ borderColor: `${theme.ring}33` }}
      data-testid={`attachment-${sectionId}-${attachment.id}`}
    >
      <button
        onClick={(e) => { if (isRenaming) return; e.stopPropagation(); onOpen(); }}
        disabled={isRenaming}
        className="flex items-center gap-3 flex-1 min-w-0 text-left"
        data-testid={`attachment-open-${sectionId}-${attachment.id}`}
      >
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: theme.bg, color: theme.text }}>
          <AttachIcon category={attachment.category} className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          {isRenaming ? (
            <input
              autoFocus
              value={renameTitle}
              onChange={e => setRenameTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') onRenameSave(); if (e.key === 'Escape') onRenameCancel(); }}
              onBlur={onRenameSave}
              onClick={e => e.stopPropagation()}
              className="w-full text-sm font-medium text-slate-800 px-2 py-1 rounded border border-[#f59e0b] bg-white focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30"
              data-testid={`attachment-rename-input-${sectionId}-${attachment.id}`}
            />
          ) : (
            <p className="text-sm font-medium text-slate-800 truncate group-hover/att:text-[#f59e0b] transition-colors">{attachment.title}</p>
          )}
          <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
            <span className="font-mono uppercase font-medium px-1.5 py-0.5 rounded" style={{ background: `${theme.ring}18`, color: theme.text }}>{attachment.extension}</span>
            <span>·</span>
            <span>{formatFileSize(attachment.size)}</span>
          </div>
        </div>
      </button>
      {!isRenaming && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {onResize && <button onClick={(e) => { e.stopPropagation(); onResize(); }} className="text-slate-400 hover:text-[#1A7F37] p-1.5 rounded-lg hover:bg-[#34A853]/10 transition-colors" title="Resize" data-testid={`attachment-resize-${sectionId}-${attachment.id}`}><Maximize2 className="w-3.5 h-3.5" /></button>}
          <button onClick={(e) => { e.stopPropagation(); onRenameStart(); }} className="text-slate-400 hover:text-[#f59e0b] p-1.5 rounded-lg hover:bg-[#f59e0b]/10 transition-colors" title="Rename" data-testid={`attachment-rename-${sectionId}-${attachment.id}`}><Edit className="w-3.5 h-3.5" /></button>
          <a href={buildAttachmentUrl(attachment.id)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-slate-400 hover:text-[#3b82f6] p-1.5 rounded-lg hover:bg-[#3b82f6]/10 transition-colors" title="Download / Open" data-testid={`attachment-download-${sectionId}-${attachment.id}`}><Download className="w-3.5 h-3.5" /></a>
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-slate-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Remove" data-testid={`attachment-delete-${sectionId}-${attachment.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );
}

// Read-only compact card for preview mode (no edit/remove actions)
export function AttachmentPreviewCard({ attachment, onOpen, accent, sectionId }) {
  const theme = ATTACH_THEMES[attachment.category] || ATTACH_THEMES.document;
  const directUrl = buildAttachmentUrl(attachment.id);
  const cat = attachment.category;
  // Inline rich previews for image / video / audio attachments so they
  // actually render in the Preview pane instead of just a filename card.
  if (cat === 'image') {
    return (
      <figure
        className="rounded-xl border bg-white overflow-hidden shadow-sm"
        style={{ borderColor: `${theme.ring}22` }}
        data-testid={`pv-attachment-${sectionId}-${attachment.id}`}>
        <button
          type="button"
          onClick={onOpen}
          className="block w-full group/att text-left"
          aria-label={`Open ${attachment.title}`}>
          <div className="w-full bg-slate-50 flex items-center justify-center" style={{ minHeight: 180 }}>
            <img
              src={directUrl}
              alt={attachment.title || 'Attachment'}
              loading="lazy"
              decoding="async"
              className="w-full max-h-[420px] object-contain"
              onError={e => {
                // Fall back to the icon card if the image fails to load.
                const wrap = e.currentTarget.parentElement;
                if (wrap) {
                  wrap.innerHTML = '';
                  wrap.style.minHeight = '120px';
                  wrap.classList.add('text-xs', 'text-slate-500');
                  wrap.textContent = 'Preview unavailable';
                }
              }}
            />
          </div>
          <div className="flex items-center gap-2 p-3 border-t" style={{ borderColor: `${theme.ring}22` }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: theme.bg, color: theme.text }}>
              <AttachIcon category={cat} className="w-4 h-4" />
            </div>
            <figcaption className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{attachment.title}</p>
              <p className="text-xs text-slate-500 font-mono uppercase">{attachment.extension} · {formatFileSize(attachment.size)}</p>
            </figcaption>
          </div>
        </button>
      </figure>
    );
  }
  if (cat === 'video') {
    return (
      <div
        className="rounded-xl border bg-white overflow-hidden shadow-sm"
        style={{ borderColor: `${theme.ring}22` }}
        data-testid={`pv-attachment-${sectionId}-${attachment.id}`}>
        <video
          src={directUrl}
          controls
          preload="metadata"
          className="w-full block bg-black"
          style={{ maxHeight: 360 }}>
          Sorry, your browser does not support inline video preview.
        </video>
        <div className="flex items-center gap-2 p-3 border-t" style={{ borderColor: `${theme.ring}22` }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: theme.bg, color: theme.text }}>
            <AttachIcon category={cat} className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{attachment.title}</p>
            <p className="text-xs text-slate-500 font-mono uppercase">{attachment.extension} · {formatFileSize(attachment.size)}</p>
          </div>
        </div>
      </div>
    );
  }
  if (cat === 'audio') {
    return (
      <div
        className="rounded-xl border bg-white p-4 shadow-sm"
        style={{ borderColor: `${theme.ring}22` }}
        data-testid={`pv-attachment-${sectionId}-${attachment.id}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: theme.bg, color: theme.text }}>
            <AttachIcon category={cat} className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{attachment.title}</p>
            <p className="text-xs text-slate-500 font-mono uppercase">{attachment.extension} · {formatFileSize(attachment.size)}</p>
          </div>
        </div>
        <audio src={directUrl} controls preload="metadata" className="w-full">
          Sorry, your browser does not support inline audio preview.
        </audio>
      </div>
    );
  }
  // Default: filename card (documents/PDFs/etc.) — click opens the viewer.
  return (
    <button
      onClick={onOpen}
      className="group/att rounded-xl border bg-white hover:border-slate-300 transition flex items-center gap-3 p-3 text-left w-full"
      style={{ borderColor: `${theme.ring}22` }}
      data-testid={`pv-attachment-${sectionId}-${attachment.id}`}
    >
      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: theme.bg, color: theme.text }}>
        <AttachIcon category={attachment.category} className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate group-hover/att:text-[#f59e0b] transition-colors">{attachment.title}</p>
        <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
          <span className="font-mono uppercase font-medium px-1.5 py-0.5 rounded" style={{ background: `${theme.ring}18`, color: theme.text }}>{attachment.extension}</span>
          <span>·</span>
          <span>{formatFileSize(attachment.size)}</span>
        </div>
      </div>
      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover/att:opacity-100 transition-opacity" style={{ background: `${accent || theme.ring}15`, color: accent || theme.ring }}>
        <ChevronRight className="w-4 h-4" />
      </div>
    </button>
  );
}

// Inline viewer — renders PDF/image/video/audio/text inline; DOCX/DOC falls back to download.
export function AttachmentViewerModal({ attachment, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const directUrl = buildAttachmentUrl(attachment.id);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    const load = async () => {
      setLoading(true); setError('');
      try {
        const token = localStorage.getItem('desky_token') || '';
        const res = await fetch(`${API}/api/teacher/studio/attachment/${attachment.id}/download`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed to load file (${res.status})`);
        if (attachment.extension === 'txt' || attachment.extension === 'rtf') {
          const txt = await res.text();
          if (!cancelled) setTextContent(txt);
        } else {
          const blob = await res.blob();
          createdUrl = URL.createObjectURL(blob);
          if (!cancelled) setBlobUrl(createdUrl);
        }
      } catch (e) {
        if (!cancelled) setError(friendlyError(e, 'Failed to load'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachment.id, attachment.extension]);

  const theme = ATTACH_THEMES[attachment.category] || ATTACH_THEMES.document;
  const isOfficeDoc = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(attachment.extension);

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="attachment-viewer-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200 bg-white">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: theme.bg, color: theme.text }}>
            <AttachIcon category={attachment.category} className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{attachment.title}</p>
            <p className="text-xs text-slate-500 truncate">{attachment.original_filename} &middot; {formatFileSize(attachment.size)} &middot; <span className="uppercase font-mono">{attachment.extension}</span></p>
          </div>
          <a href={directUrl} download={attachment.original_filename} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-white bg-[#3b82f6] hover:bg-[#2563eb] px-3 py-2 rounded-lg flex items-center gap-1.5 transition-colors" data-testid="attachment-viewer-download"><Download className="w-3.5 h-3.5" /> Download</a>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-2 rounded-lg hover:bg-slate-100" data-testid="attachment-viewer-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-hidden bg-slate-100 flex items-center justify-center relative">
          {loading && <FilePreviewSkeleton className="absolute inset-0 p-6 flex" testId="attachment-preview-loading" />}
          {!loading && error && (
            <div className="text-center p-8">
              <p className="text-sm font-medium text-red-500">{error}</p>
              <a href={directUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#3b82f6] hover:underline"><Download className="w-3.5 h-3.5" /> Try direct download</a>
            </div>
          )}
          {!loading && !error && blobUrl && attachment.category === 'image' && (
            <img src={blobUrl} alt={attachment.title} className="max-w-full max-h-full object-contain shadow-xl" data-testid="attachment-viewer-image" />
          )}
          {!loading && !error && blobUrl && attachment.extension === 'pdf' && (
            <iframe src={blobUrl} title={attachment.title} className="w-full h-full bg-white" data-testid="attachment-viewer-pdf" />
          )}
          {!loading && !error && blobUrl && attachment.category === 'video' && (
            <video src={blobUrl} controls autoPlay className="max-w-full max-h-full bg-black shadow-xl" data-testid="attachment-viewer-video" />
          )}
          {!loading && !error && blobUrl && attachment.category === 'audio' && (
            <div className="flex flex-col items-center gap-5 p-8">
              <div className="w-32 h-32 rounded-xl flex items-center justify-center shadow-xl" style={{ background: theme.bg, color: theme.text }}>
                <Music className="w-14 h-14" />
              </div>
              <p className="text-sm font-medium text-slate-700">{attachment.title}</p>
              <audio src={blobUrl} controls autoPlay className="w-96" data-testid="attachment-viewer-audio" />
            </div>
          )}
          {!loading && !error && textContent !== null && (
            <pre className="w-full h-full p-6 overflow-auto bg-white text-sm text-slate-800 font-mono whitespace-pre-wrap" data-testid="attachment-viewer-text">{textContent}</pre>
          )}
          {!loading && !error && blobUrl && isOfficeDoc && (
            <div className="text-center p-8 max-w-md">
              <div className="w-16 h-16 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ background: theme.bg, color: theme.text }}><FileText className="w-8 h-8" /></div>
              <p className="text-sm font-medium text-slate-800 mb-1">Office document</p>
              <p className="text-xs text-slate-500 mb-4">Inline preview is not supported for <span className="font-mono font-medium uppercase">{attachment.extension}</span> files. Download to open in Word / Excel / PowerPoint, or your preferred editor.</p>
              <a href={blobUrl} download={attachment.original_filename} className="inline-flex items-center gap-2 text-xs font-medium text-white bg-[#3b82f6] hover:bg-[#2563eb] px-5 py-2.5 rounded-lg transition-colors"><Download className="w-4 h-4" /> Download {attachment.original_filename}</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// SubsectionBlock — subtitle-level editor row inside a text/rich_text section.
// Renders the subtitle title + prose AND the subtitle's own charts and
// drawings. Each subsection now has independent action buttons:
//   • + Chart     — opens DeskeyChart, saves to subsection.charts[]
//   • + Drawing   — opens DeskeyPaint, saves to subsection.drawings[]
// These mirror the section-level affordances but with the data scoped to
// `subsection`, so a flowchart explaining one subtitle stays right under it.
// ────────────────────────────────────────────────────────────────────────────
function SubsectionBlock({ sub, subIdx, sectionId, sectionColor, references, onAddChart, onUpdateChart, onRemoveChart, onAttachDrawing, onRemoveDrawing, editValue, onEditChange }) {
  const [showDrawing, setShowDrawing] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [editingChartIdx, setEditingChartIdx] = useState(null);

  const charts = sub.charts || [];
  const drawings = sub.drawings || [];
  const canEdit = !!(onAddChart || onAttachDrawing);

  return (
    <div className="mt-5 pl-4 border-l-[3px]" style={{ borderColor: sectionColor || '#34A853' }} data-testid={`sub-${sectionId}-${subIdx}`}>
      {editValue ? (
        <SubsectionTextEditor value={editValue} onChange={onEditChange} testId={`sub-edit-${sectionId}-${subIdx}`} />
      ) : (
        <>
          <p className="text-sm font-medium text-slate-900 mb-2">{sub.title}</p>
          <RenderContent
            text={sub.content}
            highlights={sub.highlights}
            references={references}
            className="text-sm text-slate-600 leading-relaxed"
          />
        </>
      )}

      {/* Subtitle action bar — only shown while editing the parent draft */}
      {canEdit && (
        <div className="mt-3 flex items-center gap-2" data-testid={`sub-actions-${sectionId}-${subIdx}`}>
          <button
            onClick={() => { setEditingChartIdx(null); setShowChart(true); }}
            className="text-xs font-medium text-[#0d9488] bg-[#0d9488]/10 hover:bg-[#0d9488]/15 px-2.5 py-1 rounded-md flex items-center gap-1 transition-colors"
            data-testid={`sub-add-chart-${sectionId}-${subIdx}`}>
            <BarChart3 className="w-3 h-3" /> + Chart
          </button>
          <button
            onClick={() => setShowDrawing(true)}
            className="text-xs font-medium text-slate-600 bg-slate-500/10 hover:bg-slate-500/15 px-2.5 py-1 rounded-md flex items-center gap-1 transition-colors"
            data-testid={`sub-add-drawing-${sectionId}-${subIdx}`}>
            <Pencil className="w-3 h-3" /> + Drawing
          </button>
        </div>
      )}

      {/* Saved charts */}
      {charts.length > 0 && (
        <div className="mt-3 space-y-2.5" data-testid={`sub-charts-${sectionId}-${subIdx}`}>
          {charts.map((ch, cIdx) => (
            <div key={ch.id || cIdx} className="rounded-xl border border-[#34A853]/25 bg-white overflow-hidden shadow-sm" data-testid={`sub-chart-${sectionId}-${subIdx}-${cIdx}`}>
              <div className="flex items-center justify-between px-3 py-2 border-b border-[#34A853]/10 bg-[#34A853]/5">
                <div className="flex items-center gap-1.5 text-xs text-slate-700 min-w-0">
                  <BarChart3 className="w-3 h-3 text-[#1A7F37] flex-shrink-0" />
                  <span className="font-medium truncate">{ch.title || `Flowchart ${cIdx + 1}`}</span>
                  <span className="text-xs font-mono text-[#1A7F37]/80 bg-[#34A853]/10 px-1.5 py-0.5 rounded flex-shrink-0">{(ch.nodes || []).length}·{(ch.connectors || []).length}</span>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => { setEditingChartIdx(cIdx); setShowChart(true); }} className="text-xs font-medium text-[#1A7F37] hover:bg-[#34A853]/10 px-2 py-1 rounded flex items-center gap-1" data-testid={`sub-edit-chart-${sectionId}-${subIdx}-${cIdx}`}>
                      <Edit className="w-2.5 h-2.5" /> Edit
                    </button>
                    <button onClick={async () => { if (await confirmDialog({ title: 'Remove chart', message: 'Remove this chart?', confirmLabel: 'Remove', danger: true })) onRemoveChart && onRemoveChart(subIdx, cIdx); }} className="text-xs font-medium text-red-500 hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1" data-testid={`sub-delete-chart-${sectionId}-${subIdx}-${cIdx}`}>
                      <Trash2 className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
              </div>
              {ch.image_url && (
                <div className="p-2 bg-slate-50/50">
                  <SafeImage src={ch.image_url} alt={ch.title || 'Flowchart'} className="w-full rounded border border-slate-100 max-h-[360px] object-contain bg-white" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Saved drawings */}
      {drawings.length > 0 && (
        <div className="mt-3 space-y-2.5" data-testid={`sub-drawings-${sectionId}-${subIdx}`}>
          {drawings.map((img, iIdx) => (
            <div key={iIdx} className="rounded-xl border border-slate-300/40 bg-white overflow-hidden shadow-sm" data-testid={`sub-drawing-${sectionId}-${subIdx}-${iIdx}`}>
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200/40 bg-slate-50">
                <div className="flex items-center gap-1.5 text-xs text-slate-700 min-w-0">
                  <Pencil className="w-3 h-3 text-slate-600 flex-shrink-0" />
                  <span className="font-medium truncate">{img.caption || 'Drawing'}</span>
                </div>
                {canEdit && (
                  <button onClick={async () => { if (await confirmDialog({ title: 'Remove drawing', message: 'Remove this drawing?', confirmLabel: 'Remove', danger: true })) onRemoveDrawing && onRemoveDrawing(subIdx, iIdx); }} className="text-xs font-medium text-red-500 hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1" data-testid={`sub-delete-drawing-${sectionId}-${subIdx}-${iIdx}`}>
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>
              <div className="p-2 bg-slate-50/50">
                <SafeImage src={img.url} alt={img.caption || 'Drawing'} className="w-full rounded border border-slate-100 max-h-[360px] object-contain bg-white" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals — opened on demand */}
      {showDrawing && (
        <DeskeyPaint
          onSave={async (dataUrl) => {
            if (onAttachDrawing) await onAttachDrawing(subIdx, dataUrl);
            setShowDrawing(false);
          }}
          onClose={() => setShowDrawing(false)}
        />
      )}
      {showChart && (
        <DeskeyChart
          isEditing={editingChartIdx !== null}
          initialNodes={editingChartIdx !== null ? (charts[editingChartIdx]?.nodes || []) : null}
          initialConnectors={editingChartIdx !== null ? (charts[editingChartIdx]?.connectors || []) : null}
          onSave={async ({ dataUrl, nodes, connectors, theme }) => {
            if (editingChartIdx !== null && onUpdateChart) {
              await onUpdateChart(subIdx, editingChartIdx, { nodes, connectors, image_url: dataUrl, theme });
            } else if (onAddChart) {
              await onAddChart(subIdx, { nodes, connectors, image_url: dataUrl, theme });
            }
            setShowChart(false);
            setEditingChartIdx(null);
          }}
          onClose={() => { setShowChart(false); setEditingChartIdx(null); }}
        />
      )}
    </div>
  );
}


function FrameworkSection({ section, index, total, expanded, toggle, editingSection, editContent, setEditContent, editExtras, setEditExtras, onStartEdit, onCancelEdit, isSavingSection, saveSection, onGenerateImage, isGeneratingImage, onTargetChat, onOpenEquation, onOpenImageHelper, onDeleteEquation, draftId, onAddItem, onDeleteItem, onUpdateItem, onSaveHighlights = () => {}, onOpenVisualModal, onDeleteSection, onRemoveSectionImage, onUpdateSectionImage, onRemoveGeneratedImage, onAddSectionLink, onRemoveSectionLink, onAddTable, onUpdateTable, onRemoveTable, onSaveNotes, onAddChart, onUpdateChart, onRemoveChart, onAttachDrawing, onUploadAttachment, onRenameAttachment, onResizeAttachment, onRemoveAttachment, references, onAddSubChart, onUpdateSubChart, onRemoveSubChart, onAttachSubDrawing, onRemoveSubDrawing }) {
  const Icon = getSectionIcon(section.icon, section.title);
  const isEditing = editingSection === section.id;
  // Only text sections have inline-editable prose; item-based sections
  // (cards, flashcards, problems, …) edit per item instead.
  const isTextSection = section.type === 'text' || section.type === 'rich_text';
  const [flipped, setFlipped] = useState({});
  const [newItem, setNewItem] = useState(null);
  const [editingItemIdx, setEditingItemIdx] = useState(null);
  const [editItemData, setEditItemData] = useState(null);
  const [sectionHighlights, setSectionHighlights] = useState(section.highlights || []);
  // Follow the stored highlights when the section changes underneath us
  // (save pruned them, undo, AI edit) instead of keeping the first render's.
  useEffect(() => { setSectionHighlights(section.highlights || []); }, [section.highlights]);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  // Table builder state
  const [showTableBuilder, setShowTableBuilder] = useState(false);
  const [tbTitle, setTbTitle] = useState('');
  const [tbCols, setTbCols] = useState(3);
  const [tbRows, setTbRows] = useState(3);
  const [tbHeaders, setTbHeaders] = useState(['', '', '']);
  const [tbData, setTbData] = useState([['', '', ''], ['', '', ''], ['', '', '']]);
  // Table editing state
  const [editingTableIdx, setEditingTableIdx] = useState(null);
  const [editTableData, setEditTableData] = useState(null);
  // Notes state
  const [showNotes, setShowNotes] = useState(false);
  const [notesText, setNotesText] = useState(section.teacher_notes || '');
  const notesTimer = useRef(null);
  // Suggested images state — collapsed dropdown; fetch is deferred until the
  // teacher opens it (no auto-load on section expand).
  const [suggestedImages, setSuggestedImages] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsLoaded, setSuggestionsLoaded] = useState(false);
  const [showSuggestedImages, setShowSuggestedImages] = useState(false);
  // Section-level symbol toolbar + drawing + chart
  const [showSymbols, setShowSymbols] = useState(false);
  const [showDrawing, setShowDrawing] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [editingChartIdx, setEditingChartIdx] = useState(null);
  // Attachments state (paperclip)
  const [attachPicker, setAttachPicker] = useState(null); // {file, title} when pending
  const [attachUploading, setAttachUploading] = useState(false);
  const [viewingAttachment, setViewingAttachment] = useState(null); // attachment meta currently in viewer
  const [renameAttId, setRenameAttId] = useState(null); // id of attachment being renamed inline
  const [renameAttTitle, setRenameAttTitle] = useState('');
  const attachFileRef = useRef(null);
  // Inline reference picker — pops a list of `draft.references[]` so the
  // teacher can drop `[ref:N]` markers into the prose. The marker resolves
  // to a clickable chip in both editor preview and student MyDesk.
  const [showRefPicker, setShowRefPicker] = useState(false);
  // The action bar scrolls sideways when narrow; the reference picker lives
  // outside that scroller (or it'd be clipped) and opens under its button.
  const actionBarRef = useRef(null);
  const refBtnRef = useRef(null);
  const [refPickerLeft, setRefPickerLeft] = useState(0);
  // Line the picker up under its button, kept inside the bar's visible width.
  const placeRefPicker = () => {
    const bar = actionBarRef.current;
    const btn = refBtnRef.current;
    if (!bar || !btn) return;
    const left = btn.offsetLeft - bar.scrollLeft;
    setRefPickerLeft(Math.max(0, Math.min(left, bar.clientWidth - REF_PICKER_WIDTH)));
  };
  const SYMBOLS = ['α','β','γ','δ','ε','ζ','η','θ','λ','μ','ν','π','ρ','σ','τ','φ','ψ','ω','Δ','Γ','Σ','Φ','Ψ','Ω','∞','±','∓','×','÷','√','∛','≈','≠','≡','≤','≥','≪','≫','∝','∂','∇','∫','∑','∏','→','←','↔','↑','↓','⇒','⇐','⇔','°','²','³','⁴','⁻¹','½','¼','¾','⁺','⁻','₀','₁','₂','ℏ','Å','µ'];
  // Symbol buttons keep focus in the field (keepFocus on mousedown), so the
  // symbol lands at the caret; with no field focused it goes to the end of
  // the section being edited, else to the clipboard.
  const insertSymbol = (sym) => {
    if (insertAtCursor(document.activeElement, sym)) return;
    if (isEditing) { setEditContent((editContent || '') + sym); return; }
    navigator.clipboard?.writeText(sym).then(() => toast.success(`Copied ${sym}`));
  };
  // Insert `[ref:N]` marker at the cursor of the active textarea — or
  // append to `editContent` if no textarea is focused (switching the
  // section into edit mode first so the marker is visible immediately).
  const insertReference = async (refIdx) => {
    const marker = `[ref:${refIdx + 1}]`;
    const el = document.activeElement;
    setShowRefPicker(false);
    if (el?.tagName === 'TEXTAREA' && insertAtCursor(el, marker)) {
      // inserted at the caret
    } else if (!isTextSection) {
      toast.info('Click into a text field first, then insert the reference');
      return;
    } else if (!isEditing) {
      // May be declined when another section has unsaved edits.
      if (!(await onStartEdit((section.content || '') + ' ' + marker))) return;
    } else {
      setEditContent((editContent || '') + marker);
    }
    toast.success(`Inserted ${marker}`);
  };
  const toggleFlip = (i) => setFlipped(prev => ({ ...prev, [i]: !prev[i] }));

  // Table builder helpers
  const resetTableBuilder = () => {
    setTbTitle(''); setTbCols(3); setTbRows(3);
    setTbHeaders(['', '', '']); setTbData([['', '', ''], ['', '', ''], ['', '', '']]);
    setShowTableBuilder(false);
  };
  const updateTbSize = (cols, rows) => {
    const c = Math.max(1, Math.min(cols, 12));
    const r = Math.max(1, Math.min(rows, 30));
    setTbCols(c); setTbRows(r);
    setTbHeaders(prev => { const h = [...prev]; while (h.length < c) h.push(''); return h.slice(0, c); });
    setTbData(prev => {
      const d = prev.map(row => { const rr = [...row]; while (rr.length < c) rr.push(''); return rr.slice(0, c); });
      while (d.length < r) d.push(Array(c).fill(''));
      return d.slice(0, r);
    });
  };
  const addTbRow = () => { setTbRows(p => p + 1); setTbData(prev => [...prev, Array(tbCols).fill('')]); };
  const removeTbRow = (ri) => { if (tbRows <= 1) return; setTbRows(p => p - 1); setTbData(prev => prev.filter((_, i) => i !== ri)); };
  const addTbCol = () => { const nc = tbCols + 1; setTbCols(nc); setTbHeaders(prev => [...prev, '']); setTbData(prev => prev.map(r => [...r, ''])); };
  const removeTbCol = (ci) => { if (tbCols <= 1) return; setTbCols(p => p - 1); setTbHeaders(prev => prev.filter((_, i) => i !== ci)); setTbData(prev => prev.map(r => r.filter((_, i) => i !== ci))); };

  // Notes auto-save
  const saveNotesDebounced = (text) => {
    setNotesText(text);
    if (notesTimer.current) clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(() => { onSaveNotes(text); }, 1500);
  };

  // Toggle the Suggested Images dropdown; the API call only fires on the
  // first open, never on section expand.
  const toggleSuggestedImages = () => {
    const next = !showSuggestedImages;
    setShowSuggestedImages(next);
    if (next && !suggestionsLoaded && !loadingSuggestions && draftId && section.id) {
      setLoadingSuggestions(true);
      api(`/studio/framework/${draftId}/section/${section.id}/suggest-images`, { method: 'POST' })
        .then(res => { setSuggestedImages(res.images || []); setSuggestionsLoaded(true); })
        .catch(() => {})
        .finally(() => setLoadingSuggestions(false));
    }
  };

  // Start editing a table inline
  const startEditTable = (tIdx) => {
    const t = (section.tables || [])[tIdx];
    if (t) { setEditingTableIdx(tIdx); setEditTableData(JSON.parse(JSON.stringify(t))); }
  };

  const saveEditTable = () => {
    if (editTableData && editingTableIdx !== null) onUpdateTable(editingTableIdx, editTableData);
    setEditingTableIdx(null); setEditTableData(null);
  };
  const addEditRow = () => {
    if (!editTableData) return;
    const cols = editTableData.headers.length;
    setEditTableData(prev => ({ ...prev, rows: [...prev.rows, Array(cols).fill('')] }));
  };
  const removeEditRow = (ri) => {
    if (!editTableData || editTableData.rows.length <= 1) return;
    setEditTableData(prev => ({ ...prev, rows: prev.rows.filter((_, i) => i !== ri) }));
  };
  const addEditCol = () => {
    if (!editTableData) return;
    setEditTableData(prev => ({
      ...prev,
      headers: [...prev.headers, ''],
      rows: prev.rows.map(r => [...r, '']),
    }));
  };
  const removeEditCol = (ci) => {
    if (!editTableData || editTableData.headers.length <= 1) return;
    setEditTableData(prev => ({
      ...prev,
      headers: prev.headers.filter((_, i) => i !== ci),
      rows: prev.rows.map(r => r.filter((_, i) => i !== ci)),
    }));
  };

  // Start editing an item
  const startEditItem = (idx) => {
    setEditingItemIdx(idx);
    setEditItemData({ ...(section.items || [])[idx] });
  };
  const saveEditItem = () => {
    if (editItemData && editingItemIdx !== null) {
      const clean = (item) => (section.type === 'list' ? withSafeUrl(item) : item);
      const next = clean(editItemData);
      if (isSameItem(next, clean((section.items || [])[editingItemIdx]))) toast.info(NO_EDIT_CHANGES);
      else onUpdateItem(editingItemIdx, next);
    }
    setEditingItemIdx(null);
    setEditItemData(null);
  };
  const cancelEditItem = () => { setEditingItemIdx(null); setEditItemData(null); };

  const getEmptyItem = (type) => {
    switch(type) {
      case 'cards': return section.id === 'key_concepts' ? { term: '', definition: '', example: '' } :
        section.id === 'real_world' ? { title: '', description: '', industry: '' } :
        section.id === 'memory_aids' ? { technique: '', content: '', type: 'mnemonic' } :
        section.id === 'fun_facts' ? { fact: '', source: '' } :
        { title: '', description: '' };
      case 'flashcards': return { question: '', answer: '', difficulty: 'medium' };
      case 'problems': return { question: '', hint: '', answer: '', difficulty: 'medium' };
      case 'list': return { title: '', description: '', type: 'article', url: '' };
      default: return { title: '', content: '' };
    }
  };

  return (
    <div className={`bg-white rounded-xl overflow-hidden border transition-colors ${expanded ? 'border-slate-300' : 'border-slate-200'}`} data-testid={`section-${section.id}`}>
      <div className="flex items-center gap-3 px-6 py-4 transition-colors hover:bg-slate-50 group/sec cursor-pointer" onClick={toggle}>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${expanded ? 'bg-[#34A853]/10 text-[#1A7F37]' : 'bg-slate-100 text-slate-500'}`}>
          <Icon className="w-[18px] h-[18px]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{section.title}</p>
          <p className="text-xs text-slate-500 mt-0.5">{sectionTypeLabel(section.type)}{(section.section_images || []).length > 0 ? ` · ${section.section_images.length} img` : ''}{(section.section_links || []).length > 0 ? ` · ${section.section_links.length} link${section.section_links.length > 1 ? 's' : ''}` : ''}{(section.tables || []).length > 0 ? ` · ${section.tables.length} table${section.tables.length > 1 ? 's' : ''}` : ''}{(section.equations || []).length > 0 ? ` · ${section.equations.length} eq${section.equations.length > 1 ? 's' : ''}` : ''}{section.teacher_notes ? ' · notes' : ''}</p>
        </div>
        <span className="text-xs text-slate-400 tabular-nums">{index + 1} of {total}</span>
        <button onClick={(e) => { e.stopPropagation(); onDeleteSection(); }} className="opacity-0 group-hover/sec:opacity-100 p-2 rounded-xl text-slate-300 hover:text-rose-500 hover:bg-rose-50 transition duration-200" title="Delete section" data-testid={`delete-section-${section.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {expanded && (
        <div className="px-6 py-6" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
          {/* Action bar — one row that scrolls sideways when the editor is
              narrow (e.g. AI chat widened) instead of squashing the buttons. */}
          <div className="relative mb-5">
          <div ref={actionBarRef} onScroll={() => { if (showRefPicker) placeRefPicker(); }}
            className="relative flex items-center gap-2 overflow-x-auto pb-1.5 [&>*]:flex-shrink-0 whitespace-nowrap"
            data-testid={`section-actions-${section.id}`}>
            {isTextSection && (
              <button onClick={() => { if (!isEditing) onStartEdit(); else saveSection(); }} disabled={isSavingSection}
                className={`text-xs font-medium flex items-center gap-1.5 px-4 py-2 rounded-xl transition duration-200 active:scale-95 ${isEditing ? 'text-white shadow-sm bg-[#1A7F37]' : 'text-[#475569] bg-white border border-slate-200 hover:bg-slate-50 shadow-sm'}`}
                data-testid={`edit-section-${section.id}`}>
                {isEditing ? <><Save className="w-3.5 h-3.5" /> {isSavingSection ? 'Saving…' : 'Save'}</> : <><Edit className="w-3.5 h-3.5" /> Edit</>}
              </button>
            )}
            {isEditing && <button onClick={onCancelEdit} disabled={isSavingSection} className="text-xs font-medium text-[#475569] px-4 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 shadow-sm transition duration-200 active:scale-95">Cancel</button>}
            <button onClick={() => setShowLinkInput(!showLinkInput)} className={`text-xs font-medium flex items-center gap-1.5 px-4 py-2 rounded-xl transition duration-200 active:scale-95 shadow-sm ${showLinkInput ? 'text-slate-900 bg-slate-100 border border-slate-300' : 'text-[#475569] bg-white border border-slate-200 hover:bg-slate-50'}`} data-testid={`attach-link-section-${section.id}`}>
              <Link2 className="w-3.5 h-3.5" /> Link
            </button>
            <button onClick={() => { setShowTableBuilder(!showTableBuilder); if (!showTableBuilder) updateTbSize(3, 3); }} className={`text-xs font-medium flex items-center gap-1.5 px-4 py-2 rounded-xl transition duration-200 active:scale-95 shadow-sm ${showTableBuilder ? 'text-slate-900 bg-slate-100 border border-slate-300' : 'text-[#475569] bg-white border border-slate-200 hover:bg-slate-50'}`} data-testid={`add-table-section-${section.id}`}>
              <BarChart3 className="w-3.5 h-3.5" /> Table
            </button>
            <button onClick={() => setShowNotes(!showNotes)} className={`text-xs font-medium flex items-center gap-1.5 px-4 py-2 rounded-xl transition duration-200 active:scale-95 shadow-sm ${showNotes ? 'text-slate-900 bg-slate-100 border border-slate-300' : 'text-[#475569] bg-white border border-slate-200 hover:bg-slate-50'}`} data-testid={`notes-section-${section.id}`}>
              <FileText className="w-3.5 h-3.5" /> Notes
            </button>
            <button onMouseDown={keepFocus} onClick={() => setShowSymbols(!showSymbols)} className={`text-xs font-medium flex items-center gap-1.5 px-4 py-2 rounded-xl transition duration-200 active:scale-95 shadow-sm ${showSymbols ? 'text-slate-900 bg-slate-100 border border-slate-300' : 'text-[#475569] bg-white border border-slate-200 hover:bg-slate-50'}`} data-testid={`symbols-section-${section.id}`}>
              <span className="text-sm font-mono leading-none">fx</span> Symbols
            </button>
            {onOpenEquation && (
              <button onClick={onOpenEquation}
                className="text-xs font-medium flex items-center gap-1.5 px-4 py-2 rounded-xl transition duration-200 active:scale-95 shadow-sm text-[#475569] bg-white border border-slate-200 hover:text-green-700 hover:border-green-300 hover:bg-green-50"
                data-testid={`equation-section-${section.id}`}
                title="Open Equation Studio for this section">
                <Sigma className="w-3.5 h-3.5" /> Equation
              </button>
            )}
            <button onClick={() => setShowDrawing(true)} className="text-xs font-medium flex items-center gap-1.5 px-4 py-2 rounded-xl transition duration-200 active:scale-95 shadow-sm text-[#475569] bg-white border border-slate-200 hover:text-[#0d9488] hover:border-[#0d9488]/30 hover:bg-[#0d9488]/5" data-testid={`draw-section-${section.id}`}>
              <Pencil className="w-3.5 h-3.5" /> Draw
            </button>
            <button onClick={() => setShowChart(true)} className="text-xs font-medium flex items-center gap-1.5 px-4 py-2 rounded-xl transition duration-200 active:scale-95 shadow-sm text-[#475569] bg-white border border-slate-200 hover:text-[#1A7F37] hover:border-[#34A853]/30 hover:bg-[#34A853]/5" data-testid={`chart-section-${section.id}`}>
              <BarChart3 className="w-3.5 h-3.5" /> Chart
            </button>
            <button onClick={() => attachFileRef.current?.click()} className="text-xs font-medium flex items-center gap-1.5 px-4 py-2 rounded-xl transition duration-200 active:scale-95 shadow-sm text-[#475569] bg-white border border-slate-200 hover:text-[#f59e0b] hover:border-[#f59e0b]/30 hover:bg-[#f59e0b]/5" data-testid={`attach-section-${section.id}`}>
              <Paperclip className="w-3.5 h-3.5" /> Attach
            </button>
            {/* Insert reference chip — pops a picker showing every entry in
                draft.references; clicking inserts `[ref:N]` at the cursor of
                the active textarea (this section's content if none is focused). */}
            {Array.isArray(references) && references.length > 0 && (
              <button
                ref={refBtnRef}
                onMouseDown={keepFocus}
                onClick={() => { placeRefPicker(); setShowRefPicker(v => !v); }}
                className={`text-xs font-medium flex items-center gap-1.5 px-4 py-2 rounded-xl transition duration-200 active:scale-95 shadow-sm ${showRefPicker ? 'text-white bg-[#0d9488]' : 'text-[#475569] bg-white border border-slate-200 hover:text-[#0d9488] hover:border-[#0d9488]/30 hover:bg-[#0d9488]/5'}`}
                data-testid={`insert-ref-${section.id}`}>
                <Link2 className="w-3.5 h-3.5" /> Insert Ref
              </button>
            )}
            <input ref={attachFileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.gif,.svg,.mp4,.webm,.mov,.mp3,.wav,.m4a,.ogg" onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              if (f.size > 50 * 1024 * 1024) { toast.error('File too large. Max 50 MB'); e.target.value = ''; return; }
              setAttachPicker({ file: f, title: f.name.replace(/\.[^/.]+$/, '') });
              e.target.value = '';
            }} data-testid={`attach-input-${section.id}`} />
          </div>
          {showRefPicker && (
            <div className="absolute z-[40] top-full mt-1 max-w-full rounded-xl border border-slate-200 bg-white shadow-xl p-3 max-h-[280px] overflow-auto" style={{ left: refPickerLeft, width: REF_PICKER_WIDTH }} data-testid={`ref-picker-${section.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <p className="text-xs font-medium text-[#64748b] flex-1">Pick a reference</p>
                <button onClick={() => setShowRefPicker(false)} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
              </div>
              <ul className="space-y-1.5">
                {references.map((r, ri) => (
                  <li key={ri}>
                    <button
                      onMouseDown={keepFocus}
                      onClick={() => insertReference(ri)}
                      className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-[#0d9488]/5 border border-transparent hover:border-[#0d9488]/15 transition-colors flex items-start gap-2"
                      data-testid={`ref-picker-item-${section.id}-${ri}`}>
                      <span className="text-xs font-mono font-medium text-[#0d9488] mt-0.5">[ref:{ri + 1}]</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-xs font-medium text-slate-800 truncate">{r.title || r.url || 'Untitled'}</span>
                        {r.url && <span className="block text-xs text-slate-400 truncate">{r.url}</span>}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          </div>

          {/* Scientific Symbols Toolbar — at section top level */}
          {showSymbols && (
            <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={`symbols-panel-${section.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-slate-600">Scientific symbols</span>
                <span className="text-xs text-slate-400">Click to insert at cursor or copy</span>
                <div className="flex-1" />
                <button onClick={() => setShowSymbols(false)} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex flex-wrap gap-0.5">
                {SYMBOLS.map((sym, si) => (
                  <button key={si} onMouseDown={keepFocus} onClick={() => insertSymbol(sym)} className="w-8 h-8 rounded-lg text-base hover:bg-slate-200 hover:text-slate-900 transition-colors flex items-center justify-center text-slate-600 font-mono" title={`Insert ${sym}`} data-testid={`symbol-${si}`}>{sym}</button>
                ))}
              </div>
            </div>
          )}

          {/* Drawing Canvas Modal */}
          {showDrawing && (
            <DeskeyPaint
              onSave={async (dataUrl) => {
                if (onAttachDrawing) onAttachDrawing(dataUrl);
                toast.success('Drawing saved to section!');
                setShowDrawing(false);
              }}
              onClose={() => setShowDrawing(false)}
            />
          )}

          {/* Chart Modal (DeskeyChart — Lucidchart-style) */}
          {showChart && (
            <DeskeyChart
              isEditing={editingChartIdx !== null}
              initialNodes={editingChartIdx !== null ? (section.charts?.[editingChartIdx]?.nodes || []) : null}
              initialConnectors={editingChartIdx !== null ? (section.charts?.[editingChartIdx]?.connectors || []) : null}
              onSave={async ({ dataUrl, nodes, connectors, theme }) => {
                if (editingChartIdx !== null && onUpdateChart) {
                  onUpdateChart(editingChartIdx, { nodes, connectors, image_url: dataUrl, theme });
                } else if (onAddChart) {
                  onAddChart({ nodes, connectors, image_url: dataUrl, theme });
                }
                setShowChart(false);
                setEditingChartIdx(null);
              }}
              onClose={() => { setShowChart(false); setEditingChartIdx(null); }}
            />
          )}

          {/* Attachment Title Picker Modal */}
          {attachPicker && (
            <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => !attachUploading && setAttachPicker(null)} data-testid={`attach-title-modal-${section.id}`}>
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#f59e0b]/10 flex items-center justify-center flex-shrink-0">
                    <Paperclip className="w-4 h-4 text-[#f59e0b]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium text-slate-800">Name your attachment</p>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">{attachPicker.file.name} &middot; {formatFileSize(attachPicker.file.size)}</p>
                  </div>
                </div>
                <label className="text-xs font-medium text-slate-400 mb-1.5 block">Display Title</label>
                <input autoFocus value={attachPicker.title} onChange={e => setAttachPicker(p => ({ ...p, title: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter' && !attachUploading && attachPicker.title.trim()) { document.getElementById(`attach-submit-${section.id}`)?.click(); } }}
                  placeholder="e.g. Chapter 1 Reading Guide"
                  className="w-full text-sm px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 focus:bg-white focus:border-[#f59e0b]/40"
                  disabled={attachUploading}
                  data-testid={`attach-title-input-${section.id}`} />
                <div className="flex justify-end gap-2 mt-5">
                  <button onClick={() => setAttachPicker(null)} disabled={attachUploading} className="text-xs font-medium text-slate-600 px-4 py-2 rounded-lg hover:bg-slate-100 disabled:opacity-50">Cancel</button>
                  <button id={`attach-submit-${section.id}`}
                    disabled={attachUploading || !attachPicker.title.trim()}
                    onClick={async () => {
                      setAttachUploading(true);
                      try {
                        await onUploadAttachment(attachPicker.file, attachPicker.title.trim());
                        setAttachPicker(null);
                      } catch { /* toast already shown */ }
                      finally { setAttachUploading(false); }
                    }}
                    className="text-xs font-medium text-white bg-[#f59e0b] hover:bg-[#d97706] disabled:opacity-40 px-5 py-2 rounded-lg flex items-center gap-1.5 transition-colors"
                    data-testid={`attach-submit-btn-${section.id}`}>
                    {attachUploading ? (<><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="animate-spin"><circle cx="12" cy="12" r="10" strokeDasharray="16 48"/></svg>Uploading…</>) : (<><Paperclip className="w-3 h-3" /> Upload &amp; Attach</>)}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Attachment Viewer Modal */}
          {viewingAttachment && (
            <AttachmentViewerModal attachment={viewingAttachment} onClose={() => setViewingAttachment(null)} />
          )}

          {/* Table Builder */}
          {showTableBuilder && (
            <div className="mb-5 rounded-xl border border-[#34A853]/20 bg-[#34A853]/[0.02] p-5 space-y-4" data-testid={`table-builder-${section.id}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-[#1A7F37] flex items-center gap-1.5"><BarChart3 className="w-3 h-3" /> Create Table</p>
                <button onClick={resetTableBuilder} className="text-slate-400 hover:text-slate-600 transition-colors"><X className="w-4 h-4" /></button>
              </div>
              <input value={tbTitle} onChange={e => setTbTitle(e.target.value)} placeholder="Table title (optional)" className="w-full text-sm px-3.5 py-2.5 rounded-lg border border-[#34A853]/20 bg-white focus:outline-none focus:ring-2 focus:ring-[#34A853]/30" data-testid={`table-title-input-${section.id}`} />
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-xs font-medium text-slate-600 flex items-center gap-1.5">Columns
                  <input type="number" value={tbCols} min={1} max={12} onChange={e => updateTbSize(+e.target.value || 1, tbRows)} className="w-14 text-sm text-center px-2 py-1.5 rounded-lg border border-[#34A853]/20 bg-white focus:outline-none focus:ring-2 focus:ring-[#34A853]/30" data-testid={`table-cols-input-${section.id}`} />
                </label>
                <label className="text-xs font-medium text-slate-600 flex items-center gap-1.5">Rows
                  <input type="number" value={tbRows} min={1} max={30} onChange={e => updateTbSize(tbCols, +e.target.value || 1)} className="w-14 text-sm text-center px-2 py-1.5 rounded-lg border border-[#34A853]/20 bg-white focus:outline-none focus:ring-2 focus:ring-[#34A853]/30" data-testid={`table-rows-input-${section.id}`} />
                </label>
                <span className="text-xs text-[#1A7F37]/60 font-mono bg-[#34A853]/5 px-2 py-1 rounded">{tbCols} x {tbRows}</span>
              </div>
              <div className="overflow-x-auto rounded-lg border border-[#34A853]/15 bg-white">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th className="w-8 px-1 py-1.5 border-b border-r border-[#34A853]/10 text-xs text-[#1A7F37]/40 font-mono">#</th>
                      {tbHeaders.map((h, j) => (
                        <th key={j} className="px-1 py-1.5 border-b border-r border-[#34A853]/10 last:border-r-0 relative group/hdr">
                          <input value={h} onChange={e => { const nh = [...tbHeaders]; nh[j] = e.target.value; setTbHeaders(nh); }} placeholder={`Col ${j+1}`} className="w-full text-xs font-medium px-2 py-1 bg-transparent focus:outline-none focus:bg-white focus:ring-1 focus:ring-[#34A853]/30 rounded text-center" />
                          {tbCols > 1 && <button onClick={() => removeTbCol(j)} className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-400 text-white text-xs leading-none opacity-0 group-hover/hdr:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-500 z-10" title="Remove column">x</button>}
                        </th>
                      ))}
                      <th className="w-8 px-1 border-b border-[#34A853]/10"><button onClick={addTbCol} className="w-6 h-6 rounded-md bg-[#34A853]/10 hover:bg-[#34A853]/20 text-[#1A7F37] text-xs font-medium flex items-center justify-center transition-colors" title="Add column" data-testid={`table-add-col-${section.id}`}>+</button></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tbData.map((row, ri) => (
                      <tr key={ri} className="group/row hover:bg-[#34A853]/[0.02]">
                        <td className="px-1 py-1 border-b border-r border-[#34A853]/5 text-xs text-slate-300 text-center font-mono">{ri + 1}</td>
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-1 py-1 border-b border-r border-[#34A853]/5 last:border-r-0">
                            <input value={cell} onChange={e => { const nd = tbData.map(r => [...r]); nd[ri][ci] = e.target.value; setTbData(nd); }} placeholder="" className="w-full text-xs px-2 py-1 bg-transparent focus:outline-none focus:bg-white focus:ring-1 focus:ring-[#34A853]/30 rounded" />
                          </td>
                        ))}
                        <td className="w-8 px-1 py-1 border-b border-[#34A853]/5">
                          {tbRows > 1 && <button onClick={() => removeTbRow(ri)} className="w-5 h-5 rounded-md text-red-300 hover:text-red-500 hover:bg-red-50 text-xs opacity-0 group-hover/row:opacity-100 transition flex items-center justify-center" title="Remove row"><X className="w-3 h-3" /></button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button onClick={addTbRow} className="w-full py-1.5 text-xs font-medium text-[#1A7F37] hover:text-[#2d9249] hover:bg-[#34A853]/5 transition-colors flex items-center justify-center gap-1 border-t border-[#34A853]/10" data-testid={`table-add-row-${section.id}`}><Plus className="w-3 h-3" /> Add Row</button>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => { if (tbHeaders.some(h => h.trim())) { onAddTable({ title: tbTitle.trim(), headers: tbHeaders, rows: tbData }); resetTableBuilder(); } else { toast.error('Add at least one header'); } }} className="text-xs font-medium text-white px-5 py-2.5 rounded-lg bg-[#1A7F37] hover:bg-[#166B2E] transition-colors active:scale-95" data-testid={`table-create-btn-${section.id}`}>Create Table</button>
                <button onClick={resetTableBuilder} className="text-xs font-medium text-slate-500 px-4 py-2.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 transition">Cancel</button>
              </div>
            </div>
          )}

          {/* Notes Panel */}
          {showNotes && (
            <div className="mb-5 rounded-xl border border-yellow-200 bg-yellow-50/20 p-4 space-y-2" data-testid={`notes-panel-${section.id}`}>
              <p className="text-xs font-medium text-yellow-700 flex items-center gap-1.5"><FileText className="w-3 h-3" /> Teacher Notes <span className="text-xs font-normal text-yellow-500 ml-1">(private, not shown in preview)</span></p>
              <textarea value={notesText} onChange={e => saveNotesDebounced(e.target.value)} placeholder="Add your private notes for this section..." className="w-full h-[80px] text-sm px-3.5 py-2.5 rounded-lg border border-yellow-200 bg-white resize-none focus:outline-none focus:ring-2 focus:ring-yellow-400/30 transition" data-testid={`notes-textarea-${section.id}`} />
            </div>
          )}

          {/* Attach Link Input */}
          {showLinkInput && (
            <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50/40 p-4 space-y-2.5" data-testid={`link-input-panel-${section.id}`}>
              <p className="text-xs font-medium text-blue-700 flex items-center gap-1.5"><Link2 className="w-3 h-3" /> Attach Reference Link</p>
              <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="Paste URL (YouTube, article, website...)" className="w-full text-sm px-3.5 py-2.5 rounded-lg border border-blue-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400/30 transition" data-testid={`link-url-input-${section.id}`} />
              <input value={linkTitle} onChange={e => setLinkTitle(e.target.value)} placeholder="Link title (optional)" className="w-full text-sm px-3.5 py-2.5 rounded-lg border border-blue-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400/30 transition" data-testid={`link-title-input-${section.id}`} />
              <div className="flex gap-2">
                <button onClick={() => { if (linkUrl.trim()) { onAddSectionLink({ url: linkUrl.trim(), title: linkTitle.trim() }); setLinkUrl(''); setLinkTitle(''); setShowLinkInput(false); } }} className="text-xs font-medium text-white px-4 py-2 rounded-lg bg-[#1A7F37] hover:bg-[#166B2E] transition-colors active:scale-95" data-testid={`link-add-btn-${section.id}`}>
                  Attach Link
                </button>
                <button onClick={() => { setShowLinkInput(false); setLinkUrl(''); setLinkTitle(''); }} className="text-xs font-medium text-slate-500 px-4 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 transition">Cancel</button>
              </div>
            </div>
          )}

          {/* Text/Rich content */}
          {(section.type === 'text' || section.type === 'rich_text') && (
            <>
              {isEditing ? (
                <div className="w-full border border-slate-200 rounded-xl p-3">
                  <RichTextEditor value={editContent} onChange={setEditContent} />
                </div>
              ) : (
                <div>
                  <RenderContent text={section.content} highlights={sectionHighlights} references={references} className="text-base text-slate-700 leading-[1.85]" />
                </div>
              )}
              {sectionHighlights.length > 0 && !isEditing && (
                <div className="mt-2 flex items-center gap-1 flex-wrap">
                  <span className="text-xs text-slate-400 font-medium">Highlights:</span>
                  {sectionHighlights.map((h, i) => (
                    <span key={i} className="text-xs px-1.5 py-0.5 rounded cursor-pointer hover:opacity-70" style={{ background: h.color, color: '#374151' }}
                      onClick={async () => {
                        const updated = sectionHighlights.filter((_, j) => j !== i);
                        setSectionHighlights(updated);
                        if ((await onSaveHighlights(updated)) === false) setSectionHighlights(section.highlights || []);
                      }} title="Click to remove">
                      {h.text.slice(0, 20)}{h.text.length > 20 ? '...' : ''} x
                    </span>
                  ))}
                </div>
              )}
              {isEditing && editExtras && (
                <>
                  <LineListEditor label="Learning Objectives" items={editExtras.objectives}
                    onChange={(objectives) => setEditExtras({ ...editExtras, objectives })}
                    placeholder="What students should be able to do…" testId={`edit-objectives-${section.id}`} />
                  <LineListEditor label="Key Takeaways" items={editExtras.takeaways}
                    onChange={(takeaways) => setEditExtras({ ...editExtras, takeaways })}
                    placeholder="What students should remember…" testId={`edit-takeaways-${section.id}`} />
                </>
              )}
              {!isEditing && Array.isArray(section.objectives) && section.objectives.length > 0 && <div className="mt-5 rounded-xl p-4 border border-green-100 bg-green-50/30"><p className="text-xs font-medium text-green-700 mb-3">Learning Objectives</p><div className="space-y-2">{section.objectives.map((o, i) => <div key={i} className="flex items-start gap-2.5 text-sm text-slate-700"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-500" /><span><MathText text={o} /></span></div>)}</div></div>}
              {!isEditing && Array.isArray(section.takeaways) && section.takeaways.length > 0 && <div className="mt-4 rounded-xl p-4 border border-green-100 bg-green-50/30"><p className="text-xs font-medium text-green-700 mb-3">Key Takeaways</p><div className="space-y-2">{section.takeaways.map((t, i) => <div key={i} className="flex items-start gap-2.5 text-sm text-slate-700"><span className="font-medium text-green-600 mt-px">{i + 1}.</span><span><MathText text={t} /></span></div>)}</div></div>}
              {section.subsections?.map((sub, i) => (
                <SubsectionBlock
                  key={i}
                  sub={sub}
                  subIdx={i}
                  sectionId={section.id}
                  sectionColor={section.color}
                  references={references}
                  onAddChart={onAddSubChart}
                  onUpdateChart={onUpdateSubChart}
                  onRemoveChart={onRemoveSubChart}
                  onAttachDrawing={onAttachSubDrawing}
                  onRemoveDrawing={onRemoveSubDrawing}
                  editValue={isEditing ? editExtras?.subsections?.[i] : undefined}
                  onEditChange={(next) => setEditExtras({
                    ...editExtras,
                    subsections: editExtras.subsections.map((v, j) => (j === i ? next : v)),
                  })}
                />
              ))}
            </>
          )}

          {/* Blackboard Scan — mirrors the student BlackboardSection fields so
              the teacher sees the same body the editor never rendered before. */}
          {section.type === 'blackboard_analysis' && (
            <div className="space-y-4">
              {section.extracted_text && (
                <div className="rounded-xl p-4 border border-slate-200 bg-white relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full rounded-r bg-amber-500" />
                  <p className="text-xs font-medium mb-2.5 ml-3 text-amber-700">Extracted content</p>
                  <RenderContent text={section.extracted_text} references={references} className="ml-3 text-base text-slate-700 leading-[1.85]" />
                </div>
              )}
              {section.explanation && (
                <div className="rounded-xl p-4 border border-slate-200 bg-white relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full rounded-r bg-[#34A853]" />
                  <p className="text-xs font-medium mb-2.5 ml-3 text-[#1A7F37]">Digital explanation</p>
                  <RenderContent text={section.explanation} references={references} className="ml-3 text-base text-slate-700 leading-[1.85]" />
                </div>
              )}
              {Array.isArray(section.key_formulas) && section.key_formulas.length > 0 && (
                <div className="rounded-xl p-4 border border-slate-200 bg-white">
                  <p className="text-xs font-medium mb-2.5 text-slate-600">Key Formulas</p>
                  <div className="space-y-2">
                    {section.key_formulas.map((f, i) => (
                      <div key={i} className="px-4 py-3 rounded-lg border border-slate-100 bg-slate-50">
                        <p className="text-sm font-mono font-medium text-slate-800"><MathText text={f} /></p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(section.step_by_step) && section.step_by_step.length > 0 && (
                <div className="rounded-xl p-4 border border-slate-200 bg-white">
                  <p className="text-xs font-medium mb-2.5 text-green-600">Step-by-Step</p>
                  <div className="space-y-3">
                    {section.step_by_step.map((step, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium text-white bg-[#1A7F37]">{i + 1}</div>
                        <p className="text-sm text-slate-700 leading-relaxed pt-1"><MathText text={step} /></p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(section.concepts) && section.concepts.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {section.concepts.map((c, i) => (
                    <span key={i} className="text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 text-slate-500"><MathText text={c} /></span>
                  ))}
                </div>
              )}
              {section.diagram_description && (
                <div className="rounded-xl p-4 border border-slate-200 bg-white">
                  <p className="text-xs font-medium mb-2.5 text-slate-600">Diagram Description</p>
                  <p className="text-sm text-slate-700 leading-relaxed"><MathText text={section.diagram_description} /></p>
                </div>
              )}
            </div>
          )}

          {/* Structured equations attached to this section (math · physics · chemistry) */}
          {Array.isArray(section.equations) && section.equations.length > 0 && (
            <div className="mt-5">
              <SectionEquations equations={section.equations} onDelete={onDeleteEquation} compact />
            </div>
          )}

          {/* Cards — Click to Edit */}
          {section.type === 'cards' && <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">{(section.items||[]).map((item, i) => (
              editingItemIdx === i ? (
                <div key={i} className="rounded-lg border-2 border-[#34A853]/30 p-3 bg-[#34A853]/5 space-y-2" style={{ borderLeft: `3px solid #34A853` }}>
                  {Object.keys(item).map(k => (
                    <EditFieldInput key={k} value={editItemData?.[k] || ''} onChange={v => setEditItemData(prev => ({ ...prev, [k]: v }))} placeholder={k.charAt(0).toUpperCase() + k.slice(1)} />
                  ))}
                  <div className="flex gap-2 pt-1">
                    <button onClick={saveEditItem} className="text-xs font-medium text-white px-3 py-1 rounded-md" style={{ background: G }} data-testid={`save-item-${section.id}-${i}`}><Save className="w-3 h-3 inline mr-1" />Save</button>
                    <button onClick={cancelEditItem} className="text-xs font-medium text-slate-500 px-3 py-1 rounded-md hover:bg-slate-200">Cancel</button>
                  </div>
                </div>
              ) : (
                <div key={i} className="rounded-xl border border-slate-200 p-4 hover:shadow-md transition group/item relative cursor-pointer" style={{ borderLeft: `3px solid #34A853` }}
                  onClick={() => startEditItem(i)} data-testid={`edit-item-${section.id}-${i}`}>
                  <button onClick={(e) => { e.stopPropagation(); onDeleteItem(i); }} className="absolute top-2 right-2 opacity-0 group-hover/item:opacity-100 text-slate-300 hover:text-red-500 transition z-10" data-testid={`delete-item-${section.id}-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  {(item.term || item.title || item.technique) && <p className="text-sm font-medium text-slate-900 mb-1.5"><MathText text={item.term || item.title || item.technique} /></p>}
                  {(item.fact || item.definition || item.description || item.content) && <p className="text-sm text-slate-600 leading-relaxed"><MathText text={item.fact || item.definition || item.description || item.content} /></p>}
                  {item.example && <p className="text-xs text-slate-500 mt-2">e.g. <MathText text={item.example} /></p>}
                  {item.industry && <span className="text-xs font-medium mt-2 inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{item.industry}</span>}
                </div>
              )
            ))}</div>
            {/* Add new item */}
            {newItem && newItem.type === section.id ? (
              <div className="rounded-lg border-2 border-dashed border-slate-300 p-3 bg-slate-50">
                <div className="space-y-2">
                  {Object.keys(getEmptyItem(section.type)).map(k => (
                    <input key={k} value={newItem.data[k] || ''} onChange={e => setNewItem(prev => ({ ...prev, data: { ...prev.data, [k]: e.target.value }}))}
                      placeholder={k.charAt(0).toUpperCase() + k.slice(1)} className="w-full text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white" />
                  ))}
                  <div className="flex gap-2">
                    <button onClick={() => { if (Object.values(newItem.data).some(v => v.trim())) { onAddItem(newItem.data); } setNewItem(null); }}
                      className="text-xs font-medium text-white px-3 py-1 rounded-md" style={{ background: G }}>Add</button>
                    <button onClick={() => setNewItem(null)} className="text-xs font-medium text-slate-500 px-3 py-1 rounded-md hover:bg-slate-200">Cancel</button>
                  </div>
                </div>
              </div>
            ) : (
              <button onClick={() => setNewItem({ type: section.id, data: getEmptyItem(section.type) })}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-slate-300 text-xs font-medium text-slate-400 hover:text-slate-600 hover:border-slate-400 transition-colors" data-testid={`add-item-${section.id}`}>
                <Plus className="w-3 h-3" /> Add Item
              </button>
            )}
          </div>}

          {/* Visual — Open in Full Modal */}
          {section.type === 'visual' && <div className="space-y-3">
            {(section.generated_images||[]).length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                {section.generated_images.slice(0, 2).map((img, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-slate-200 cursor-pointer hover:shadow-md transition-shadow relative group" data-testid={`gen-img-preview-${section.id}-${i}`}>
                    <SafeImage src={img.url} alt="" className="w-full aspect-video object-cover" zoomable={false} onClick={onOpenVisualModal} skeleton={false} />
                    <button onClick={(e) => { e.stopPropagation(); onRemoveGeneratedImage(i); }} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600" data-testid={`delete-gen-img-${section.id}-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {(section.image_prompts||[]).length > 0 && (section.generated_images||[]).length === 0 && (
              <div className="text-center py-3">
                <p className="text-xs text-slate-500 mb-1">{section.image_prompts.length} visual elements ready to generate</p>
              </div>
            )}
            <button onClick={onOpenVisualModal}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-slate-200 text-sm font-medium text-slate-600 hover:text-slate-700 hover:border-slate-400 hover:bg-slate-50/50 transition" data-testid={`open-visual-modal-${section.id}`}>
              <Image className="w-4 h-4" /> Open Visual Editor
              {(section.generated_images||[]).length > 0 && <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full">{section.generated_images.length} images</span>}
            </button>
          </div>}

          {/* Flashcards — Click to Edit */}
          {section.type === 'flashcards' && <div className="space-y-3">{(section.items||[]).map((item, i) => {
            const d = DIFF[item.difficulty] || DIFF.easy;
            if (editingItemIdx === i) return (
              <div key={i} className="rounded-xl border-2 border-[#34A853]/30 p-4 bg-[#34A853]/5 space-y-2">
                <EditFieldInput value={editItemData?.question || ''} onChange={v => setEditItemData(prev => ({ ...prev, question: v }))} placeholder="Question" multiline />
                <EditFieldInput value={editItemData?.answer || ''} onChange={v => setEditItemData(prev => ({ ...prev, answer: v }))} placeholder="Answer" multiline />
                <div className="flex gap-2 items-center">
                  <select value={editItemData?.difficulty || 'medium'} onChange={e => setEditItemData(prev => ({ ...prev, difficulty: e.target.value }))} className="text-xs px-2 py-1.5 rounded-md border border-[#34A853]/20 bg-white" onClick={e => e.stopPropagation()}>
                    <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
                  </select>
                  <button onClick={saveEditItem} className="text-xs font-medium text-white px-3 py-1.5 rounded-md ml-auto" style={{ background: G }}><Save className="w-3 h-3 inline mr-1" />Save</button>
                  <button onClick={cancelEditItem} className="text-xs font-medium text-slate-500 px-3 py-1.5 rounded-md hover:bg-slate-200">Cancel</button>
                </div>
              </div>
            );
            return (
              <div key={i} className="cursor-pointer rounded-xl border border-slate-200 overflow-hidden transition hover:shadow-md group/card relative" style={{ background: flipped[i] ? '#f0fdf4' : 'white' }} data-testid={`flashcard-${i}`}>
                <button onClick={(e) => { e.stopPropagation(); onDeleteItem(i); }} className="absolute top-2 right-2 opacity-0 group-hover/card:opacity-100 text-slate-300 hover:text-red-500 transition z-10" data-testid={`delete-flashcard-${i}`}><Trash2 className="w-3 h-3" /></button>
                <button onClick={(e) => { e.stopPropagation(); startEditItem(i); }} className="absolute top-2 right-7 opacity-0 group-hover/card:opacity-100 text-slate-300 hover:text-blue-500 transition z-10" data-testid={`edit-flashcard-${i}`}><Edit className="w-3 h-3" /></button>
                <div className="p-4" onClick={() => toggleFlip(i)}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: d.bg, color: d.text }}>{d.l}</span>
                    <span className="text-xs font-medium text-slate-400">{flipped[i] ? 'ANSWER' : 'QUESTION'}</span>
                  </div>
                  <p className="text-sm text-slate-900 font-medium"><MathText text={flipped[i] ? item.answer : item.question} /></p>
                  <p className="text-xs text-slate-400 mt-2">{flipped[i] ? 'Click to see question' : 'Click to reveal'}</p>
                </div>
              </div>
            );
          })}
            <button onClick={() => setNewItem({ type: section.id, data: getEmptyItem(section.type) })}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-slate-300 text-xs font-medium text-slate-400 hover:text-slate-600 hover:border-slate-400 transition-colors" data-testid={`add-item-${section.id}`}>
              <Plus className="w-3 h-3" /> Add Flashcard
            </button>
            {newItem && newItem.type === section.id && (
              <div className="rounded-lg border-2 border-dashed border-orange-300 p-3 bg-orange-50/50">
                <div className="space-y-2">
                  <input value={newItem.data.question || ''} onChange={e => setNewItem(prev => ({ ...prev, data: { ...prev.data, question: e.target.value }}))}
                    placeholder="Question" className="w-full text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white" />
                  <input value={newItem.data.answer || ''} onChange={e => setNewItem(prev => ({ ...prev, data: { ...prev.data, answer: e.target.value }}))}
                    placeholder="Answer" className="w-full text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white" />
                  <select value={newItem.data.difficulty || 'medium'} onChange={e => setNewItem(prev => ({ ...prev, data: { ...prev.data, difficulty: e.target.value }}))}
                    className="text-xs px-2 py-1 rounded-md border border-slate-200 bg-white">
                    <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
                  </select>
                  <div className="flex gap-2">
                    <button onClick={() => { if (newItem.data.question?.trim()) { onAddItem(newItem.data); } setNewItem(null); }}
                      className="text-xs font-medium text-white px-3 py-1 rounded-md" style={{ background: G }}>Add</button>
                    <button onClick={() => setNewItem(null)} className="text-xs font-medium text-slate-500 px-3 py-1 rounded-md hover:bg-slate-200">Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>}

          {/* Problems — Click to Edit */}
          {section.type === 'problems' && <div className="space-y-3">{(section.items||[]).map((item, i) => {
            const d = DIFF[item.difficulty] || DIFF.easy;
            if (editingItemIdx === i) return (
              <div key={i} className="rounded-xl border-2 border-[#34A853]/30 p-4 bg-[#34A853]/5 space-y-2">
                <EditFieldInput value={editItemData?.question || ''} onChange={v => setEditItemData(prev => ({ ...prev, question: v }))} placeholder="Question" multiline />
                <EditFieldInput value={editItemData?.hint || ''} onChange={v => setEditItemData(prev => ({ ...prev, hint: v }))} placeholder="Hint (optional)" />
                <EditFieldInput value={editItemData?.answer || ''} onChange={v => setEditItemData(prev => ({ ...prev, answer: v }))} placeholder="Answer" multiline />
                <div className="flex gap-2 items-center">
                  <select value={editItemData?.difficulty || 'medium'} onChange={e => setEditItemData(prev => ({ ...prev, difficulty: e.target.value }))} className="text-xs px-2 py-1.5 rounded-md border border-[#34A853]/20 bg-white" onClick={e => e.stopPropagation()}>
                    <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
                  </select>
                  <button onClick={saveEditItem} className="text-xs font-medium text-white px-3 py-1.5 rounded-md ml-auto" style={{ background: G }}><Save className="w-3 h-3 inline mr-1" />Save</button>
                  <button onClick={cancelEditItem} className="text-xs font-medium text-slate-500 px-3 py-1.5 rounded-md hover:bg-slate-200">Cancel</button>
                </div>
              </div>
            );
            return (
              <div key={i} className="rounded-xl border border-slate-200 p-4 group/prob relative">
                <button onClick={() => onDeleteItem(i)} className="absolute top-2 right-2 opacity-0 group-hover/prob:opacity-100 text-slate-300 hover:text-red-500 transition" data-testid={`delete-problem-${i}`}><Trash2 className="w-3 h-3" /></button>
                <button onClick={() => startEditItem(i)} className="absolute top-2 right-7 opacity-0 group-hover/prob:opacity-100 text-slate-300 hover:text-blue-500 transition" data-testid={`edit-problem-${i}`}><Edit className="w-3 h-3" /></button>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2"><span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: d.bg, color: d.text }}>{d.l}</span></div>
                  <span className="text-xs font-medium text-slate-400">#{i + 1}</span>
                </div>
                <p className="text-sm font-medium text-slate-900 mb-2"><MathText text={item.question} /></p>
                {item.hint && <p className="text-xs text-[#1A7F37] bg-[#34A853]/5 px-2.5 py-1.5 rounded-lg mb-2">Hint: <MathText text={item.hint} /></p>}
                <button onClick={() => toggleFlip(`p${i}`)} className="text-xs font-medium text-green-600 hover:underline">{flipped[`p${i}`] ? 'Hide Answer' : 'Show Answer'}</button>
                {flipped[`p${i}`] && <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-100"><p className="text-xs text-green-800"><MathText text={item.answer} /></p></div>}
              </div>
            );
          })}
            <button onClick={() => setNewItem({ type: section.id, data: getEmptyItem(section.type) })}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-slate-300 text-xs font-medium text-slate-400 hover:text-slate-600 hover:border-slate-400 transition-colors" data-testid={`add-item-${section.id}`}>
              <Plus className="w-3 h-3" /> Add Problem
            </button>
            {newItem && newItem.type === section.id && (
              <div className="rounded-lg border-2 border-dashed border-red-200 p-3 bg-red-50/30">
                <div className="space-y-2">
                  <input value={newItem.data.question || ''} onChange={e => setNewItem(prev => ({ ...prev, data: { ...prev.data, question: e.target.value }}))}
                    placeholder="Question" className="w-full text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white" />
                  <input value={newItem.data.hint || ''} onChange={e => setNewItem(prev => ({ ...prev, data: { ...prev.data, hint: e.target.value }}))}
                    placeholder="Hint (optional)" className="w-full text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white" />
                  <input value={newItem.data.answer || ''} onChange={e => setNewItem(prev => ({ ...prev, data: { ...prev.data, answer: e.target.value }}))}
                    placeholder="Answer" className="w-full text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white" />
                  <div className="flex gap-2 items-center">
                    <select value={newItem.data.difficulty || 'medium'} onChange={e => setNewItem(prev => ({ ...prev, data: { ...prev.data, difficulty: e.target.value }}))}
                      className="text-xs px-2 py-1 rounded-md border border-slate-200 bg-white">
                      <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
                    </select>
                    <button onClick={() => { if (newItem.data.question?.trim()) { onAddItem(newItem.data); } setNewItem(null); }}
                      className="text-xs font-medium text-white px-3 py-1 rounded-md ml-auto" style={{ background: G }}>Add</button>
                    <button onClick={() => setNewItem(null)} className="text-xs font-medium text-slate-500 px-3 py-1 rounded-md hover:bg-slate-200">Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>}

          {/* List — Click to Edit */}
          {section.type === 'list' && <div className="space-y-2">{(section.items||[]).map((item, i) => (
            editingItemIdx === i ? (
              <div key={i} className="rounded-lg border-2 border-[#34A853]/30 p-3 bg-[#34A853]/5 space-y-2">
                <EditFieldInput value={editItemData?.title || ''} onChange={v => setEditItemData(prev => ({ ...prev, title: v }))} placeholder="Title" />
                <EditFieldInput value={editItemData?.description || ''} onChange={v => setEditItemData(prev => ({ ...prev, description: v }))} placeholder="Description" />
                <ResourceLinkField value={editItemData?.url} onChange={v => setEditItemData(prev => ({ ...prev, url: v }))} testId={`list-item-url-${i}`} />
                <div className="flex gap-2 items-center">
                  <select value={editItemData?.type || 'article'} onChange={e => setEditItemData(prev => ({ ...prev, type: e.target.value }))} className="text-xs px-2 py-1.5 rounded-md border border-[#34A853]/20 bg-white" onClick={e => e.stopPropagation()}>
                    <option value="article">Article</option><option value="video">Video</option><option value="course">Course</option><option value="research">Research</option><option value="textbook">Textbook</option>
                  </select>
                  <button onClick={saveEditItem} disabled={isInvalidLink(editItemData?.url)} className="text-xs font-medium text-white px-3 py-1.5 rounded-md ml-auto disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: G }} data-testid={`save-list-item-${i}`}><Save className="w-3 h-3 inline mr-1" />Save</button>
                  <button onClick={cancelEditItem} className="text-xs font-medium text-slate-500 px-3 py-1.5 rounded-md hover:bg-slate-200">Cancel</button>
                </div>
              </div>
            ) : (
              <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100 group/li relative cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => startEditItem(i)} data-testid={`edit-list-item-${i}`}>
                <button onClick={(e) => { e.stopPropagation(); onDeleteItem(i); }} className="absolute top-1.5 right-1.5 opacity-0 group-hover/li:opacity-100 text-slate-300 hover:text-red-500 transition z-10" data-testid={`delete-list-${i}`}><Trash2 className="w-3 h-3" /></button>
                <div className="absolute top-1.5 right-6 opacity-0 group-hover/li:opacity-100 text-slate-300 transition"><Edit className="w-3 h-3" /></div>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#34A853]/10"><BookOpen className="w-4 h-4 text-[#1A7F37]" /></div>
                <div className="flex-1"><p className="text-xs font-medium text-slate-900"><MathText text={item.title} /></p><p className="text-xs text-slate-500"><MathText text={item.description} /></p></div>
                <span className="text-xs font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{item.type}</span>
              </div>
            )
          ))}
            <button onClick={() => setNewItem({ type: section.id, data: getEmptyItem(section.type) })}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-slate-300 text-xs font-medium text-slate-400 hover:text-slate-600 hover:border-slate-400 transition-colors" data-testid={`add-item-${section.id}`}>
              <Plus className="w-3 h-3" /> Add Resource
            </button>
            {newItem && newItem.type === section.id && (
              <div className="rounded-lg border-2 border-dashed border-slate-300 p-3 bg-slate-50">
                <div className="space-y-2">
                  <input value={newItem.data.title || ''} onChange={e => setNewItem(prev => ({ ...prev, data: { ...prev.data, title: e.target.value }}))}
                    placeholder="Title" className="w-full text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white" />
                  <input value={newItem.data.description || ''} onChange={e => setNewItem(prev => ({ ...prev, data: { ...prev.data, description: e.target.value }}))}
                    placeholder="Description" className="w-full text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white" />
                  <ResourceLinkField value={newItem.data.url} onChange={v => setNewItem(prev => ({ ...prev, data: { ...prev.data, url: v }}))} testId={`new-list-item-url-${section.id}`} />
                  <div className="flex gap-2">
                    <select value={newItem.data.type || 'article'} onChange={e => setNewItem(prev => ({ ...prev, data: { ...prev.data, type: e.target.value }}))}
                      className="text-xs px-2 py-1 rounded-md border border-slate-200 bg-white">
                      <option value="article">Article</option><option value="video">Video</option><option value="course">Course</option><option value="research">Research</option><option value="textbook">Textbook</option>
                    </select>
                    <button onClick={() => { if (newItem.data.title?.trim()) { onAddItem(withSafeUrl(newItem.data)); } setNewItem(null); }}
                      disabled={isInvalidLink(newItem.data.url)}
                      className="text-xs font-medium text-white px-3 py-1 rounded-md disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: G }} data-testid={`add-list-item-${section.id}`}>Add</button>
                    <button onClick={() => setNewItem(null)} className="text-xs font-medium text-slate-500 px-3 py-1 rounded-md hover:bg-slate-200">Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>}

          {/* Saved Charts — with Edit/Remove; rendered after content, at the
              bottom of the section, alongside Attachments/Tables/Images */}
          {(section.charts || []).length > 0 && (
            <div className="mt-6 flex flex-wrap gap-3" data-testid={`charts-area-${section.id}`}>
              {section.charts.map((ch, cIdx) => (
                <div key={ch.id || cIdx} style={{ flex: `1 1 ${ch.width || 100}%`, minWidth: 260 }} className="rounded-xl border border-[#34A853]/25 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`chart-${section.id}-${cIdx}`}>
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#34A853]/10 bg-[#34A853]/5">
                    <div className="flex items-center gap-2 text-xs text-slate-700 min-w-0">
                      <BarChart3 className="w-4 h-4 text-[#1A7F37] flex-shrink-0" />
                      <span className="font-medium truncate">{ch.title || `Flowchart ${cIdx + 1}`}</span>
                      <span className="text-xs font-mono text-[#1A7F37]/80 bg-[#34A853]/10 px-1.5 py-0.5 rounded flex-shrink-0">{(ch.nodes || []).length} shapes · {(ch.connectors || []).length} lines</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <ResizeButton width={ch.width || 100} onClick={() => onUpdateChart(cIdx, { width: nextWidth(ch.width || 100) })} />
                      <button onClick={() => { setEditingChartIdx(cIdx); setShowChart(true); }} className="text-xs font-medium text-[#1A7F37] bg-[#34A853]/10 hover:bg-[#34A853]/20 px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors" data-testid={`edit-chart-${section.id}-${cIdx}`}><Edit className="w-3 h-3" /> Edit</button>
                      <button onClick={async () => { if (await confirmDialog({ title: 'Remove chart', message: 'Remove this chart?', confirmLabel: 'Remove', danger: true })) onRemoveChart(cIdx); }} className="text-xs font-medium text-red-500 hover:bg-red-50 px-2.5 py-1.5 rounded-lg flex items-center gap-1 transition-colors" data-testid={`delete-chart-${section.id}-${cIdx}`}><Trash2 className="w-3 h-3" /> Remove</button>
                    </div>
                  </div>
                  {ch.image_url && ch.image_url.length > 500 && (ch.nodes || []).length > 0 ? (
                    <div className="p-3 bg-slate-50/50">
                      <SafeImage src={ch.image_url} alt={ch.title || 'Flowchart'} className="w-full rounded border border-slate-100 max-h-[480px] object-contain bg-white" data-testid={`chart-img-${section.id}-${cIdx}`} />
                    </div>
                  ) : (
                    <div className="p-6 bg-slate-50/50 flex flex-col items-center justify-center text-center gap-2" data-testid={`chart-empty-${section.id}-${cIdx}`}>
                      <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-amber-500" />
                      </div>
                      {(ch.nodes || []).length > 0 ? (
                        <>
                          <p className="text-xs font-medium text-slate-700">Chart preview unavailable</p>
                          <p className="text-xs text-slate-500 max-w-sm">Your chart has {(ch.nodes || []).length} shape{(ch.nodes || []).length !== 1 ? 's' : ''} saved. Click <b>Edit</b> to view or modify — then Save to refresh the thumbnail.</p>
                        </>
                      ) : (
                        <>
                          <p className="text-xs font-medium text-slate-700">This chart has no editable data yet</p>
                          <p className="text-xs text-slate-500 max-w-sm">Click <b>Edit</b> to build it from a template, AI Generate, or draw from scratch.</p>
                        </>
                      )}
                      <button onClick={() => { setEditingChartIdx(cIdx); setShowChart(true); }} className="mt-1 text-xs font-medium text-white bg-[#1A7F37] hover:bg-[#166B2E] px-4 py-1.5 rounded-lg transition-colors" data-testid={`chart-empty-edit-${section.id}-${cIdx}`}>{(ch.nodes || []).length > 0 ? 'Open in Editor' : 'Build Now'}</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Saved Attachments — clickable cards with inline viewer */}
          {(section.attachments || []).length > 0 && (
            <div className="mt-6 space-y-2" data-testid={`attachments-area-${section.id}`}>
              <p className="text-xs font-medium text-[#1A7F37] flex items-center gap-1.5 px-1"><Paperclip className="w-3 h-3" /> Attachments</p>
              <div className="flex flex-wrap gap-2.5">
                {section.attachments.map((att) => (
                  <div key={att.id} style={{ flex: `1 1 ${att.width || 50}%`, minWidth: 240 }}>
                    <AttachmentCard
                      attachment={att}
                      onOpen={() => setViewingAttachment(att)}
                      onRenameStart={() => { setRenameAttId(att.id); setRenameAttTitle(att.title || ''); }}
                      onRemove={async () => { if (await confirmDialog({ title: 'Remove attachment', message: `Remove "${att.title}"?`, confirmLabel: 'Remove', danger: true })) onRemoveAttachment(att.id); }}
                      isRenaming={renameAttId === att.id}
                      renameTitle={renameAttTitle}
                      setRenameTitle={setRenameAttTitle}
                      onRenameCancel={() => { setRenameAttId(null); setRenameAttTitle(''); }}
                      onRenameSave={() => {
                        const t = (renameAttTitle || '').trim();
                        if (t && t !== att.title) onRenameAttachment(att.id, t);
                        setRenameAttId(null); setRenameAttTitle('');
                      }}
                      onResize={() => onResizeAttachment(att.id, att.title, nextWidth(att.width || 50))}
                      sectionId={section.id}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section Tables — shown for ALL section types */}
          {(section.tables || []).length > 0 && (
            <div className="mt-6 space-y-4" data-testid={`tables-area-${section.id}`}>
              {section.tables.map((t, i) => {
                const isEditMode = editingTableIdx === i;
                const tbl = isEditMode ? editTableData : t;
                const C = TABLE_DEFAULT_COLORS;
                return (
                <div key={i} className="overflow-x-auto rounded-xl border group/tbl relative shadow-sm" style={{ borderColor: `${C.border}33` }} data-testid={`table-${section.id}-${i}`}>
                  {/* Table header bar */}
                  <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ background: C.header_bg, borderColor: `${C.border}20` }}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <BarChart3 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: C.accent }} />
                      {isEditMode ? (
                        <input value={editTableData?.title || ''} onChange={e => setEditTableData(prev => ({ ...prev, title: e.target.value }))} placeholder="Table title" className="text-sm font-medium bg-white border rounded-lg px-3 py-1 focus:outline-none focus:ring-2 flex-1 min-w-0" style={{ color: C.header_text, borderColor: `${C.border}30` }} />
                      ) : (
                        <p className="text-sm font-medium truncate" style={{ color: C.header_text }}>{t.title || 'Table'}</p>
                      )}
                      <span className="text-xs font-mono flex-shrink-0 px-1.5 py-0.5 rounded" style={{ background: `${C.accent}14`, color: C.accent }}>{(tbl.headers||[]).length} x {(tbl.rows||[]).length}</span>
                    </div>
                    <div className="flex items-center gap-1.5 ml-2">
                      {isEditMode ? (
                        <>
                          <button onClick={saveEditTable} className="text-xs font-medium text-white px-3 py-1.5 rounded-lg shadow-sm transition hover:shadow-md active:scale-95" style={{ background: C.accent }} data-testid={`save-table-${section.id}-${i}`}><Save className="w-3 h-3 inline mr-1" />Save</button>
                          <button onClick={() => { setEditingTableIdx(null); setEditTableData(null); }} className="text-xs font-medium text-slate-500 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 transition">Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEditTable(i)} className="opacity-0 group-hover/tbl:opacity-100 text-xs font-medium px-2.5 py-1.5 rounded-lg hover:bg-white transition flex items-center gap-1" style={{ color: C.header_text }} data-testid={`edit-table-${section.id}-${i}`}><Edit className="w-3 h-3" /> Edit</button>
                          <button onClick={() => onRemoveTable(i)} className="opacity-0 group-hover/tbl:opacity-100 text-xs font-medium text-red-400 px-2 py-1.5 rounded-lg hover:bg-red-50 transition" data-testid={`delete-table-${section.id}-${i}`}><Trash2 className="w-3 h-3" /></button>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Table grid */}
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr style={{ background: C.header_bg }}>
                        {isEditMode && <th className="w-8 px-1 py-2.5 border-b border-r text-xs font-mono" style={{ borderColor: `${C.border}20`, color: `${C.accent}66` }}>#</th>}
                        {(tbl.headers || []).map((h, j) => (
                          <th key={j} className="text-left px-4 py-2.5 border-b border-r last:border-r-0 font-medium text-xs relative group/thdr" style={{ borderColor: `${C.border}22`, color: C.header_text }}>
                            {isEditMode ? (
                              <input value={h} onChange={e => { const nh = [...editTableData.headers]; nh[j] = e.target.value; setEditTableData(prev => ({ ...prev, headers: nh })); }} className="w-full text-xs font-medium bg-transparent focus:outline-none focus:bg-white focus:ring-1 rounded px-1 py-0.5" style={{ color: C.header_text }} />
                            ) : <MathText text={h} />}
                            {isEditMode && editTableData.headers.length > 1 && (
                              <button onClick={() => removeEditCol(j)} className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-400 text-white text-xs leading-none opacity-0 group-hover/thdr:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-500 z-10" title="Remove column">x</button>
                            )}
                          </th>
                        ))}
                        {isEditMode && (
                          <th className="w-10 px-1 py-2.5 border-b" style={{ borderColor: `${C.border}22` }}>
                            <button onClick={addEditCol} className="w-7 h-7 rounded-lg text-sm font-medium flex items-center justify-center transition-colors mx-auto" style={{ background: `${C.accent}1a`, color: C.accent }} title="Add column" data-testid={`edit-add-col-${section.id}-${i}`}>+</button>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {(tbl.rows || []).map((r, j) => (
                        <tr key={j} className={`border-b last:border-0 ${isEditMode ? 'group/erow' : ''}`} style={{ background: j % 2 === 1 ? C.even_row_bg : C.odd_row_bg, borderColor: `${C.border}14` }}>
                          {isEditMode && (
                            <td className="px-1 py-2 border-r text-xs text-center font-mono relative" style={{ borderColor: `${C.border}10`, color: `${C.text}66` }}>
                              {j + 1}
                              {editTableData.rows.length > 1 && (
                                <button onClick={() => removeEditRow(j)} className="absolute -left-0.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-red-400 text-white text-xs leading-none opacity-0 group-hover/erow:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-500 z-10" title="Remove row">x</button>
                              )}
                            </td>
                          )}
                          {r.map((c, k) => (
                            <td key={k} className={`px-4 py-2.5 border-r last:border-r-0 text-sm ${k === 0 ? 'font-medium' : ''}`} style={{ borderColor: `${C.border}10`, color: C.text }}>
                              {isEditMode ? (
                                <input value={c} onChange={e => { const nd = editTableData.rows.map(row => [...row]); nd[j][k] = e.target.value; setEditTableData(prev => ({ ...prev, rows: nd })); }} className="w-full text-sm bg-transparent focus:outline-none focus:bg-white focus:ring-2 rounded px-1.5 py-0.5 transition" style={{ color: C.text }} />
                              ) : <MathText text={c} />}
                            </td>
                          ))}
                          {isEditMode && <td className="w-10" style={{ borderColor: `${C.border}10` }} />}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {isEditMode && (
                    <button onClick={addEditRow} className="w-full py-2 text-xs font-medium transition-colors flex items-center justify-center gap-1 border-t" style={{ color: C.accent, borderColor: `${C.border}18` }} data-testid={`edit-add-row-${section.id}-${i}`}><Plus className="w-3 h-3" /> Add Row</button>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {/* AI Suggested Images — dropdown; images load only when opened */}
          <div className="mt-6" data-testid={`suggested-images-${section.id}`}>
            <button
              type="button"
              onClick={toggleSuggestedImages}
              className="flex items-center gap-1.5 text-xs font-medium text-[#64748b] hover:text-slate-600 transition-colors"
              data-testid={`suggested-images-toggle-${section.id}`}>
              {showSuggestedImages ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              Suggested images
              {suggestionsLoaded && <span className="text-slate-400 normal-case tracking-normal">({suggestedImages.length})</span>}
            </button>
            {showSuggestedImages && (
              <div className="mt-2.5">
                {loadingSuggestions ? (
                  <div className="flex items-center gap-2 text-xs text-slate-500"><InlineSparkle size={18} label="Finding images" /> Finding images for this section…</div>
                ) : suggestedImages.length === 0 ? (
                  <p className="text-xs text-slate-400">No image suggestions for this section.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {suggestedImages.slice(0, 6).map((img, i) => (
                      <div key={i} className="rounded-lg overflow-hidden border border-slate-100 hover:shadow-md transition group/simg relative aspect-video bg-slate-50">
                        <img src={img.url} alt={img.description} className="w-full h-full object-cover" loading="lazy" onError={e => {
                          // Hide ONLY the broken image, not the parent card — keeps
                          // the grid intact when a single CDN url fails.
                          e.target.style.display = 'none';
                          const ph = e.target.nextElementSibling;
                          if (ph) ph.style.display = 'flex';
                        }} />
                        <div className="absolute inset-0 hidden items-center justify-center text-xs text-slate-400 bg-slate-100"
                          style={{ display: 'none' }}>
                          <span className="flex items-center gap-1"><Image className="w-3 h-3" /> {img.description?.slice(0, 32) || 'Image'}</span>
                        </div>
                        <button onClick={() => onAttachDrawing(img.url, img.description || 'Image').then(ok => ok && toast.success('Image attached'))}
                          className="absolute inset-0 bg-black/0 group-hover/simg:bg-black/40 flex items-center justify-center opacity-0 group-hover/simg:opacity-100 transition" data-testid={`attach-suggested-${section.id}-${i}`}>
                          <span className="text-white text-xs font-medium px-3 py-1.5 rounded-lg" style={{ background: G }}><Plus className="w-3 h-3 inline mr-0.5" /> Add</span>
                        </button>
                        <p className="absolute bottom-0 left-0 right-0 text-xs text-white bg-black/50 px-1.5 py-0.5 truncate opacity-0 group-hover/simg:opacity-100 transition">{img.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section Links */}
          {(section.section_links || []).length > 0 && (
            <div className="mt-6 space-y-2.5" data-testid={`section-links-${section.id}`}>
              <p className="text-xs font-medium text-[#64748b] flex items-center gap-1.5"><Link2 className="w-3 h-3 text-blue-500" /> Reference Links ({section.section_links.length})</p>
              <div className="space-y-2">
                {section.section_links.map((link, i) => {
                  const isYT = link.link_type === 'youtube' && link.youtube_id;
                  return (
                    <div key={i} className="group/link flex items-center gap-3 rounded-xl border border-slate-100 bg-[#f8fafc] hover:bg-blue-50/40 hover:border-blue-200 p-3 transition" data-testid={`section-link-${section.id}-${i}`}>
                      {isYT ? (
                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="w-20 h-14 rounded-lg overflow-hidden flex-shrink-0 relative bg-black cursor-pointer">
                          <img src={`https://img.youtube.com/vi/${link.youtube_id}/mqdefault.jpg`} alt="" className="w-full h-full object-cover opacity-80" />
                          <div className="absolute inset-0 flex items-center justify-center"><PlayCircle className="w-7 h-7 text-white drop-shadow-lg" /></div>
                        </a>
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0"><Globe className="w-4 h-4 text-blue-600" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#1e293b] hover:text-blue-600 transition-colors truncate block w-full text-left" title={link.url}>{link.title || link.url}</a>
                        <p className="text-xs text-[#94a3b8] truncate mt-0.5">{link.url}</p>
                      </div>
                      <button onClick={() => onRemoveSectionLink(i)} className="opacity-0 group-hover/link:opacity-100 p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition" title="Remove link" data-testid={`remove-link-${section.id}-${i}`}><X className="w-3.5 h-3.5" /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Generated Images — AI-generated images added via Image Studio's "Add To".
              Visual sections render these in their own grid above; every other
              section type shows them here so an image added to any section is visible. */}
          {section.type !== 'visual' && (section.generated_images || []).length > 0 && (
            <div className="mt-6 space-y-3" data-testid={`generated-images-${section.id}`}>
              <p className="text-xs font-medium text-[#64748b] flex items-center gap-1.5">Generated images ({section.generated_images.length})</p>
              <div className="grid grid-cols-1 gap-3">
                {section.generated_images.map((img, i) => (
                  <div key={i} className="rounded-xl overflow-hidden group/img relative border border-slate-100 bg-[#f5f8f7] shadow-[0_4px_24px_rgba(0,0,0,0.02)]" data-testid={`generated-image-${section.id}-${i}`}>
                    <SafeImage src={img.url} alt="Generated image"
                      className="w-full object-contain max-h-[300px]"
                    />
                    <button onClick={() => onRemoveGeneratedImage(i)} className="absolute top-3 right-3 p-2 rounded-xl text-white/90 hover:bg-red-500 opacity-0 group-hover/img:opacity-100 transition duration-200 shadow-sm" style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)' }} title="Remove image" data-testid={`remove-gen-img-${section.id}-${i}`}><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section Images */}
          {(section.section_images || []).length > 0 && (
            <div className="mt-6 space-y-3">
              <p className="text-xs font-medium text-[#64748b] flex items-center gap-1.5"><Image className="w-3 h-3 text-[#0d9488]" /> Section Images ({section.section_images.length})</p>
              <div className="flex flex-wrap gap-3">
                {section.section_images.map((img, i) => (
                  <div key={i} style={{ flex: `1 1 ${img.width || 100}%`, minWidth: 220 }} className="rounded-xl overflow-hidden group/img relative border border-slate-100 bg-[#f5f8f7] shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
                    <SafeImage src={img.url} alt={img.caption || 'Section image'}
                      className="w-full object-contain max-h-[300px]"
                    />
                    <div className="absolute top-3 left-3 opacity-0 group-hover/img:opacity-100 transition">
                      <ResizeButton width={img.width || 100} onClick={() => onUpdateSectionImage(i, { width: nextWidth(img.width || 100) })} dark />
                    </div>
                    <button onClick={() => onRemoveSectionImage(i)} className="absolute top-3 right-3 p-2 rounded-xl text-white/90 hover:bg-red-500 opacity-0 group-hover/img:opacity-100 transition duration-200 shadow-sm" style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)' }} title="Remove image" data-testid={`remove-img-${section.id}-${i}`}><X className="w-3.5 h-3.5" /></button>
                    {img.caption && <p className="text-xs text-[#475569] px-4 py-2.5 bg-white border-t border-slate-100">{img.caption}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default FrameworkSection;
